<template>
  <div>
    <nav
      ref="nav"
      tabindex="0"
      :class="[
        $style.nav,
        {
          [$style.navIsCenter]: isCentered,
          [$style.navIsTall]: isTall,
        },
      ]"
      @keydown.esc="focusNav"
    >
      <b-pagination
        class="b-pagination-version2"
        :model-value="modelValue"
        :total-rows="totalRows"
        :per-page="perPage"
        :limit="limit"
        :aria-controls="ariaControls"
        :aria-label="resolvedAriaLabel"
        :label-first-page="resolvedLabelFirstPage"
        :label-prev-page="resolvedLabelPrevPage"
        :label-page="resolvedLabelPage"
        :label-next-page="resolvedLabelNextPage"
        :label-last-page="resolvedLabelLastPage"
        first-number
        last-number
        next-class="next-element"
        prev-class="prev-element"
        @update:model-value="(value) => $emit('update:modelValue', Number(value))"
      >
        <template #prev-text="{ disabled }">
          <lv-button
            tag="span"
            styling="small-primary-dark-just-icon"
            :disabled-style="disabled"
            class="awPaginationVersion2Arrow"
          >
            <lv-icon name="arrow-left-24" :size="16" />
          </lv-button>
        </template>
        <template #next-text="{ disabled }">
          <lv-button
            tag="span"
            styling="small-primary-dark-just-icon"
            :disabled-style="disabled"
            class="awPaginationVersion2Arrow"
          >
            <lv-icon name="arrow-right-24" :size="16" />
          </lv-button>
        </template>
      </b-pagination>
    </nav>
  </div>
</template>

<script setup lang="ts">
  import { computed, useTemplateRef } from 'vue';
  import { useNuxtApp } from 'nuxt/app';
  import { LvButton, LvIcon } from '~~/common/components/loginet-vue-shop/index';

  // The <nav> landmark is the single root, so DOM concerns supplied by callers (class, tabindex,
  // @keydown, ...) fall through to it. Data inputs for <b-pagination> are explicit props.
  const props = withDefaults(defineProps<{
    modelValue?: number;
    totalRows: number;
    perPage: number;
    limit?: number;
    // Horizontally centers the pagination row.
    isCentered?: boolean;
    isTall?: boolean;
    ariaControls: string;
    ariaLabel?: string;
    labelFirstPage?: string;
    labelPrevPage?: string;
    labelPage?: string;
    labelNextPage?: string;
    labelLastPage?: string;
  }>(), {
    modelValue: 1,
    limit: undefined,
    isCentered: false,
    isTall: true,
    ariaControls: undefined,
    ariaLabel: undefined,
    labelFirstPage: undefined,
    labelPrevPage: undefined,
    labelPage: undefined,
    labelNextPage: undefined,
    labelLastPage: undefined,
  });

  defineEmits<{
    'update:modelValue': [value: number];
  }>();

  const { $awt } = useNuxtApp();
  const resolvedAriaLabel = computed(() => props.ariaLabel ?? $awt('aw.pagination.label'));
  const resolvedLabelFirstPage = computed(() => props.labelFirstPage ?? $awt('aw.pagination.first'));
  const resolvedLabelPrevPage = computed(() => props.labelPrevPage ?? $awt('aw.pagination.prev'));
  const resolvedLabelPage = computed(() => props.labelPage ?? $awt('aw.pagination.page'));
  const resolvedLabelNextPage = computed(() => props.labelNextPage ?? $awt('aw.pagination.next'));
  const resolvedLabelLastPage = computed(() => props.labelLastPage ?? $awt('aw.pagination.last'));

  const navRef = useTemplateRef('nav');

  // Keyboard users navigating the pages can press Esc to move focus out to the nav landmark
  // (which is focusable via tabindex), rather than being trapped among the page buttons.
  function focusNav () {
    navRef.value?.focus();
  }
</script>

<style module lang="scss" rel="stylesheet/scss">
@mixin focusVisible() {
  border-radius: 3px;
  box-shadow: 0 0 0 1px $color-white, -1px -1px 0 3px $color-black;
}

.nav {
  margin: 3px;

  &:focus-visible {
    @include focusVisible();
  }
}

.navIsCenter {
  display: flex;
  justify-content: center;
}

.navIsTall {
  padding: 16px 0;
}

// target bootstrap-vue-next generated markup (.page-item / .page-link) and shared literal class
// names, so they are declared :global.
:global {
  .b-pagination-version2 {
    font-family: $secondary-font;
    align-items: center;
    gap: 12px;
    margin: 0;
    color: $color-text-secondary;

    .page-item {
      --paginationVersion2PagMargin: 14px;

      @include tablet(min) {
        --paginationVersion2PagMargin: 24px;
      }

      .page-link {
        font-size: 14px;
        font-weight: $font-weight-medium-v2;
        line-height: 20px;
        margin: 0 2px;
        padding: 0;
        color: $color-text-secondary;
        background: transparent;
      }

      &:not(.next-element, .prev-element) {
        .page-link {
          min-width: 16px;
          height: 20px;
        }
      }

      &.active {
        .page-link {
          color: $color-text-primary;
          border-bottom: 2px solid $color-text-primary;
          background: transparent;
        }
      }

      &.prev-element {
        margin-right: var(--paginationVersion2PagMargin);
      }

      &.next-element {
        margin-left: var(--paginationVersion2PagMargin);
      }
    }

    .awPaginationVersion2Arrow {
      display: flex;
    }

    .page-link:focus-visible,
    .page-link:focus-visible > .awPaginationVersion2Arrow {
      @include focusVisible();
    }
  }
}
</style>
